import { bool, elementType, func, number, oneOfType, string } from "prop-types";
import {
  forwardRef,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import styled from "styled-components";

import { colorsByFunction } from "UI/colors";

import { addOpacity } from "Utilities/Colors/colorUtils";
import useWidth from "Utilities/Hooks/useWidth";
import dynOpactity from "Utilities/interpolations/dynOpactity";
import retainInputCursorPos from "Utilities/retainInputCursorPos";

import AttachedError from "./AttachedError";
import DefaultIcon from "./Decorators/DefaultIcon";
import DefaultInput from "./Inputs/DefaultInput";
import DefaultLabel from "./Labels/DefaultLabel";

const GROW_MEASURE_STYLE = {
  position: "absolute",
  visibility: "hidden",
  whiteSpace: "pre",
  pointerEvents: "none",
};

const FnrInput3 = forwardRef(
  /**
   * Flocknote's canonical input component. All additional props are forwarded to whatever is passed to the Componet prop.
   *
   * @param {object} param0
   * @param {import("react").ElementType} param0.Component - the component that should be used for the input. Defaults to StandardInput.
   * @param {import("react").ElementType} param0.LabelComponent - the component that should be used for the label. Is passed `hasValue`, `label`, `left`, and `inputRef` for props and a ref.
   * @param {import("react").ElementType} param0.PrefixComponent - the component preceeding the editable section of the input. Is passed `icon` and `inputRef` for props and a ref.
   * @param {import("react").ElementType} param0.SuffixComponent - the component proceeding the editable section of the input. Is passed `inputRef` for props and a ref.
   * @param {string} param0.value - the value of the input if controlling the component.
   * @param {function} param0.setValue - a function that takes in a string as the first arg and sets `value` to it.
   * @param {function} param0.onChange - onChange is fired just as if would be natively on the passed in Component
   * @param {string} param0.label - passed to LabelComponent
   * @param {string} param0.icon - passed to PrefixComponent
   * @param {string} param0.className - here so that we can style the wrapper component. To style the input, passed in a styled component to Component.
   * @param {number} param0.charLimit - the number of charaters the input is allowed to be
   * @param {function} param0.onBlur - onBlur is fired just as it would be natively on the passed in Component
   * @param {string | boolean} param0.error - error message to display below the input, or `true` to show error styling without a message
   * @param {number} param0.growMode - minimum width in pixels; when set, the input dynamically grows to fit its content
   * @param {import("react").RefObject<HTMLInputElement>} forwardedRef
   * */
  (
    {
      Component = DefaultInput,
      LabelComponent = DefaultLabel,
      PrefixComponent = DefaultIcon,
      SuffixComponent,
      value: valueArg = "",
      setValue: setValueArg,
      onChange: onChangeArg,
      onBlur: onBlurArg,
      label,
      icon,
      className,
      charLimit = Infinity,
      error,
      growMode,
      ...rest
    },
    forwardedRef
  ) => {
    /** @type import("react").RefObject<HTMLInputElement> */
    const localRef = useRef(null);
    const ref = forwardedRef || localRef;
    const prefixRef = useRef();
    const prefixWidth = useWidth(prefixRef);
    const suffixRef = useRef();
    const suffixWidth = useWidth(suffixRef);
    const growRef = useRef();
    const [localValue, localSetValue] = useState(setValueArg ? "" : valueArg);
    const [showLengthWarning, setShowLengthWarning] = useState(false);
    const [growWidth, setGrowWidth] = useState(growMode || undefined);

    const onChange = useCallback(
      (e) => {
        if (charLimit < e.target.value.length) {
          setShowLengthWarning(true);
          if ((localValue || valueArg).length < charLimit) {
            (setValueArg || localSetValue)(e.target.value.slice(0, charLimit));
          }
          retainInputCursorPos(ref.current, { backOne: true });
          return;
        }
        setShowLengthWarning(false);
        if (onChangeArg) {
          onChangeArg(e);
        }
        (setValueArg || localSetValue)(e.target.value);
      },
      [charLimit, localValue, onChangeArg, ref, setValueArg, valueArg]
    );

    const onBlur = useCallback(
      (e) => {
        setShowLengthWarning(false);
        onBlurArg?.(e);
      },
      [onBlurArg]
    );

    const value = useMemo(() => valueArg || localValue, [valueArg, localValue]);

    useLayoutEffect(() => {
      if (!growMode || !ref.current || !growRef.current) return;
      const input = ref.current;
      const span = growRef.current;
      const displayedValue = input.value || input.placeholder || "";
      const inputStyles = getComputedStyle(input);
      span.style.fontSize = inputStyles.fontSize;
      span.style.fontFamily = inputStyles.fontFamily;
      span.style.fontWeight = inputStyles.fontWeight;
      span.style.letterSpacing = inputStyles.letterSpacing;
      span.textContent = displayedValue;
      const textWidth = span.getBoundingClientRect().width;
      const paddingLeft = parseFloat(inputStyles.paddingLeft) || 0;
      const paddingRight = parseFloat(inputStyles.paddingRight) || 0;
      const borderLeft = parseFloat(inputStyles.borderLeftWidth) || 0;
      const borderRight = parseFloat(inputStyles.borderRightWidth) || 0;
      const totalWidth = Math.ceil(
        textWidth + paddingLeft + paddingRight + borderLeft + borderRight
      );
      setGrowWidth(Math.max(growMode, totalWidth));
    }, [growMode, value, ref]);

    const refsArePopulated = typeof prefixWidth === "number";
    const displayError =
      error ||
      (showLengthWarning ? `Only ${charLimit} characters are allowed` : null);

    return (
      <InputRoot data-cy="InputWrapper" $opacity={refsArePopulated ? 1 : 0}>
        <InputContent
          data-cy="InputContent"
          className={className}
          data-has-error={displayError ? "" : undefined}
          style={growMode ? { width: growWidth, flexShrink: 0 } : undefined}
        >
          <LabelComponent
            inputRef={ref}
            label={label}
            hasValue={Boolean(value)}
            left={prefixWidth}
            icon={icon}
          />
          <PrefixComponent ref={prefixRef} inputRef={ref} icon={icon} />
          <Component
            $paddingLeft={prefixWidth || undefined}
            $paddingRight={
              suffixWidth
                ? `calc(${suffixWidth}px + 2 * var(--fnr-input-suffix-inset))`
                : undefined
            }
            ref={ref}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            {...rest}
          />
          {SuffixComponent && (
            <SuffixComponent ref={suffixRef} inputRef={ref} />
          )}
          {growMode && <span ref={growRef} style={GROW_MEASURE_STYLE} />}
        </InputContent>
        <ErrorSlot>
          <AttachedError error={displayError} />
        </ErrorSlot>
      </InputRoot>
    );
  }
);

const InputRoot = styled.div`
  ${dynOpactity}
`;

const InputContent = styled.div`
  position: relative; /* allows label and icon to move about freely. */
  display: flex;
  flex-direction: column;

  /* The label, the notch behind it, and the suffix are siblings of the input,
     so they can't see its styles. Everything reads these tokens instead, the
     input included (standardInputStyles). To restyle an input's padding or
     background, set the tokens on this wrapper (styled(FnrInput3) lands here)
     rather than on the input, or the label drifts. GetInvolvedForm.jsx does the
     focus colour that way. */
  --fnr-input-bg: #fff;
  --fnr-input-padding-x: 16px;
  --fnr-input-padding-y: 12px;
  --fnr-input-suffix-inset: 6px;
  /* where a top-aligned label rests (the 1px is the border). Derived here and
     not in DefaultLabel because motion animates the numbers inside a calc()
     string and leaves the var() in place, but resolves a bare var() whole. */
  --fnr-input-label-top: calc(var(--fnr-input-padding-y) + 1px);

  &[data-has-error] input,
  &[data-has-error] textarea {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    border-color: ${colorsByFunction.warningBorder};
  }

  &[data-has-error] input:hover,
  &[data-has-error] textarea:hover {
    box-shadow: 0 0 4px 2px ${addOpacity(colorsByFunction.warningBorder, 0.3)};
  }

  &[data-has-error] input:focus,
  &[data-has-error] textarea:focus {
    box-shadow: 0 0 4px 2px ${colorsByFunction.warningBorder};
  }
`;

const ErrorSlot = styled.div`
  width: 0;
  min-width: 100%;
`;

FnrInput3.propTypes = {
  Component: elementType,
  LabelComponent: elementType,
  PrefixComponent: elementType,
  SuffixComponent: elementType,
  value: oneOfType([string, number]),
  setValue: func,
  onChange: func,
  onBlur: func,
  label: string,
  icon: string,
  className: string,
  charLimit: number,
  error: oneOfType([string, bool]),
  growMode: number,
};

export default FnrInput3;
